import type { ActionEnvironment } from '@/domain/actionEnvironment';
import { CHAT_CLIENT_CONNECTION_STATUSES } from '@/domain/app';
import type { WidgetStore } from '@/stores/storeTypes';

/** Fallback for a turn that ends with nothing that clears the status (handoff, lost typing:stop). */
export const AGENT_ACTIVITY_TTL_MS = 60_000;

/**
 * glados sends interim text ("Let me check that") alongside the tool call on
 * another socket, so it can land just after the status. While the tool runs,
 * agent output this soon after a status does not clear it.
 */
export const AGENT_ACTIVITY_GRACE_MS = 1_000;

let nextToken = 0;

/** Shows a Beacon tool call's status beside the typing dots; ignored when no conversation is live. */
export function beaconToolStatusReceived(env: ActionEnvironment, { callId, status }: ToolStatusPayload): void {
  env.logAction('messages/beaconToolStatusReceived', { callId, status });

  const { gateways, store } = env;
  const { chatClientConnectionStatus, isSessionEnded } = store.app;
  if (
    !gateways.chatGateway.getSessionId() ||
    isSessionEnded ||
    chatClientConnectionStatus === CHAT_CLIENT_CONNECTION_STATUSES.DISCONNECTED ||
    chatClientConnectionStatus === CHAT_CLIENT_CONNECTION_STATUSES.SILENTLY_DISCONNECTED
  ) {
    return;
  }

  const token = ++nextToken;
  store.appMutations.setAgentActivity({
    activity: { callId, shownAt: Date.now(), text: status, toolRunning: true, token },
  });
  // Keyed on the token so it never clears a newer status with the same text.
  setTimeout(() => {
    if (store.app.agentActivity?.token === token) {
      store.appMutations.setAgentActivity({ activity: null });
    }
  }, AGENT_ACTIVITY_TTL_MS);
}

interface ToolStatusPayload {
  callId: string;
  status: string;
}

/** Marks the status as done once its tool call has sent its result, so the answer that follows clears it. */
export function beaconToolCallSettled(env: ActionEnvironment, { callId }: { callId: string }): void {
  env.logAction('messages/beaconToolCallSettled', { callId });

  const { app, appMutations } = env.store;
  if (app.agentActivity?.callId === callId) {
    appMutations.setAgentActivity({ activity: { ...app.agentActivity, toolRunning: false } });
  }
}

type ActivityStore = Pick<WidgetStore, 'app' | 'appMutations'>;

/** No-op when nothing is showing. */
export function clearAgentActivity(store: ActivityStore): void {
  if (store.app.agentActivity !== null) {
    store.appMutations.setAgentActivity({ activity: null });
  }
}

/**
 * Clears on agent output (a message or a stream delta), except output that
 * lands within the grace window while the status's tool is still running:
 * that is the interim text sent alongside the call. Once the tool has settled
 * the status no longer describes anything in progress, so any output clears
 * it; for a fast tool that may still be the interim text, so the label goes
 * before the answer arrives. A tool still running after the window cannot
 * hold the status over an answer the server sent without it.
 */
export function clearAgentActivityOnAgentOutput(store: ActivityStore): void {
  const activity = store.app.agentActivity;
  if (activity && !(activity.toolRunning && Date.now() - activity.shownAt < AGENT_ACTIVITY_GRACE_MS)) {
    store.appMutations.setAgentActivity({ activity: null });
  }
}
